import type {
  AuthMsg,
  ClientMessage,
  ErrorCode,
  ServerMessage,
  ServerMessageType,
} from "../api/types";

/**
 * Все типы серверных сообщений. `satisfies` заставляет обновить список, когда
 * бэкенд добавит или уберёт сообщение (после `pnpm gen` сборка упадёт здесь).
 */
const SERVER_MESSAGE_TYPES = {
  state: true,
  peer_joined: true,
  peer_left: true,
  await_filters: true,
  deck: true,
  empty_deck: true,
  match: true,
  no_match: true,
  other_progress: true,
  error: true,
} satisfies Record<ServerMessageType, true>;

/** Ошибки, после которых переподключаться бессмысленно. */
const FATAL_ERRORS: ReadonlySet<ErrorCode> = new Set<ErrorCode>([
  "INVALID_INIT_DATA",
  "NOT_SUBSCRIBED",
  "ROOM_NOT_FOUND",
  "ROOM_FULL",
]);

/** Сервер закрывает старое соединение кодом 4000, когда игрок открыл комнату в другом месте. */
const CLOSE_REPLACED = 4000;
const WS_OPEN = 1;

export type CloseReason = ErrorCode | "REPLACED" | "UNREACHABLE" | "CLOSED_BY_USER";

export type SocketStatus =
  | { readonly state: "idle" }
  /** Сокет открывается / ждём первый `state` после авторизации. */
  | { readonly state: "connecting"; readonly attempt: number }
  | { readonly state: "ready" }
  /** Связь потеряна; следующая попытка через `delayMs`. */
  | { readonly state: "reconnecting"; readonly attempt: number; readonly delayMs: number }
  | { readonly state: "closed"; readonly reason: CloseReason };

export type SendResult = "sent" | "queued" | "dropped";

export interface RoomSocketOptions {
  /** ws(s)://host/ws/ROOM — см. `roomSocketUrl`. */
  url: string;
  getInitData: () => string;
  webSocket?: new (url: string) => WebSocket;
  baseDelayMs?: number;
  maxDelayMs?: number;
  /** Доля случайного разброса задержки (0.3 = ±30%). */
  jitter?: number;
  /** Сколько подряд неудачных попыток до статуса `closed/UNREACHABLE`. */
  maxAttempts?: number;
  /** Сколько сообщений хранить, пока связи нет (старые вытесняются). */
  maxQueue?: number;
  random?: () => number;
}

export function parseServerMessage(raw: unknown): ServerMessage | null {
  if (typeof raw !== "string") return null;
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof data !== "object" || data === null) return null;
  const type = (data as { type?: unknown }).type;
  if (typeof type !== "string" || !Object.hasOwn(SERVER_MESSAGE_TYPES, type)) return null;
  return data as ServerMessage;
}

/**
 * Клиент комнаты: авторизация первым сообщением, переподключение с
 * экспоненциальной паузой, очередь исходящих на время обрыва.
 *
 * Совместим с `useSyncExternalStore`: `status` возвращает неизменяемый объект,
 * который заменяется только при реальной смене состояния.
 *
 * ВАЖНО для потребителя: после каждого переподключения приходит новый `state`.
 * Применяйте его идемпотентно и досылайте лайки, которых нет в `state.my_likes`
 * (голос на сервере идемпотентен).
 */
export class RoomSocket {
  readonly #opts: Required<Omit<RoomSocketOptions, "webSocket">>;
  readonly #WS: new (url: string) => WebSocket;

  #status: SocketStatus = { state: "idle" };
  #ws: WebSocket | null = null;
  #timer: ReturnType<typeof setTimeout> | null = null;
  #attempt = 0;
  #fatal: ErrorCode | null = null;
  #queue: ClientMessage[] = [];
  readonly #messageListeners = new Set<(message: ServerMessage) => void>();
  readonly #statusListeners = new Set<() => void>();

  constructor(options: RoomSocketOptions) {
    this.#opts = {
      baseDelayMs: 500,
      maxDelayMs: 5000,
      jitter: 0.3,
      maxAttempts: 10,
      maxQueue: 50,
      random: Math.random,
      ...options,
    };
    this.#WS = options.webSocket ?? WebSocket;
  }

  get status(): SocketStatus {
    return this.#status;
  }

  connect(): void {
    if (this.#status.state !== "idle" && this.#status.state !== "closed") return;
    this.#fatal = null;
    this.#attempt = 0;
    this.#open();
  }

  close(): void {
    if (this.#timer) clearTimeout(this.#timer);
    this.#timer = null;
    this.#queue = [];
    const ws = this.#ws;
    this.#ws = null; // поздние события старого сокета будут проигнорированы
    ws?.close(1000);
    this.#setStatus({ state: "closed", reason: "CLOSED_BY_USER" });
  }

  send(message: ClientMessage): SendResult {
    const { state } = this.#status;
    if (state === "idle" || state === "closed") return "dropped";
    if (state === "ready" && this.#ws?.readyState === WS_OPEN) {
      this.#ws.send(JSON.stringify(message));
      return "sent";
    }
    this.#queue.push(message);
    if (this.#queue.length > this.#opts.maxQueue) this.#queue.shift();
    return "queued";
  }

  onMessage(listener: (message: ServerMessage) => void): () => void {
    this.#messageListeners.add(listener);
    return () => this.#messageListeners.delete(listener);
  }

  /** Подходит как `subscribe` для `useSyncExternalStore`. */
  onStatusChange(listener: () => void): () => void {
    this.#statusListeners.add(listener);
    return () => this.#statusListeners.delete(listener);
  }

  // ------------------------------------------------------------------ внутреннее

  #open(): void {
    const ws = new this.#WS(this.#opts.url);
    this.#ws = ws;
    this.#setStatus({ state: "connecting", attempt: this.#attempt });

    ws.onopen = () => {
      if (this.#ws !== ws) return;
      const auth: AuthMsg = { type: "auth", init_data: this.#opts.getInitData() };
      ws.send(JSON.stringify(auth));
    };
    ws.onmessage = (event: MessageEvent) => {
      if (this.#ws !== ws) return;
      this.#handle(event.data);
    };
    ws.onclose = (event: CloseEvent) => {
      if (this.#ws !== ws) return;
      this.#ws = null;
      this.#onClosed(event.code);
    };
    // Ошибка всегда сопровождается close — отдельно ничего не делаем.
    ws.onerror = () => undefined;
  }

  #handle(raw: unknown): void {
    const message = parseServerMessage(raw);
    if (!message) return;

    if (message.type === "error" && FATAL_ERRORS.has(message.code)) {
      this.#fatal = message.code;
    }
    const becameReady = message.type === "state" && this.#status.state === "connecting";
    if (becameReady) {
      this.#attempt = 0;
      this.#setStatus({ state: "ready" });
    }
    this.#emit(message);
    if (becameReady) this.#flush();
  }

  #flush(): void {
    const pending = this.#queue;
    this.#queue = [];
    for (const message of pending) this.send(message);
  }

  #onClosed(code: number): void {
    if (this.#fatal) {
      this.#setStatus({ state: "closed", reason: this.#fatal });
    } else if (code === CLOSE_REPLACED) {
      this.#setStatus({ state: "closed", reason: "REPLACED" });
    } else {
      this.#scheduleReconnect();
    }
  }

  #scheduleReconnect(): void {
    this.#attempt += 1;
    if (this.#attempt > this.#opts.maxAttempts) {
      this.#setStatus({ state: "closed", reason: "UNREACHABLE" });
      return;
    }
    const { baseDelayMs, maxDelayMs, jitter, random } = this.#opts;
    const base = Math.min(maxDelayMs, baseDelayMs * 2 ** (this.#attempt - 1));
    const delayMs = Math.round(base * (1 + jitter * (2 * random() - 1)));
    this.#setStatus({ state: "reconnecting", attempt: this.#attempt, delayMs });
    this.#timer = setTimeout(() => {
      this.#timer = null;
      this.#open();
    }, delayMs);
  }

  #setStatus(next: SocketStatus): void {
    if (JSON.stringify(next) === JSON.stringify(this.#status)) return;
    this.#status = next;
    for (const listener of [...this.#statusListeners]) listener();
  }

  #emit(message: ServerMessage): void {
    for (const listener of [...this.#messageListeners]) {
      try {
        listener(message);
      } catch (error) {
        console.error("RoomSocket: ошибка в обработчике сообщения", error);
      }
    }
  }
}
